import type {
  ReportCatalog,
  ReportMeasure,
  ReportPoint,
  ReportQuery,
  ReportQueryResult,
  ReportRow
} from "@gettactic/api";

/** A second measure's answer, drawn beside the first's. */
export type PairedResult = {
  measure: ReportMeasure;
  result: ReportQueryResult;
};

const dimensionsUsed = (query: ReportQuery): string[] => [
  ...(query.breakdown ?? []),
  ...(query.filters ?? []).map((f) => f.dimension)
];

/**
 * Whether a measure can be drawn beside the query's own: counted in the
 * same kind and unit, so both read on one axis, and taking every dimension
 * the query is cut or filtered by, so both answer the same question.
 */
export const canPair = (
  own: ReportMeasure,
  other: ReportMeasure,
  query: ReportQuery
): boolean =>
  other.key !== own.key &&
  other.kind === own.kind &&
  other.unit === own.unit &&
  dimensionsUsed(query).every((d) => other.dimensions.includes(d));

/** The measures that can be drawn beside the query's, in catalog order. */
export const pairableMeasures = (
  catalog: ReportCatalog,
  query: ReportQuery
): ReportMeasure[] => {
  const own = catalog.measures.find((m) => m.key === query.measure);
  if (!own) {
    return [];
  }
  return catalog.measures.filter((m) => !m.hidden && canPair(own, m, query));
};

/**
 * The question for the second measure: the same cut, filters and period,
 * without a comparison (a pair is its own comparison). Null when nothing is
 * paired, the measure is unknown, or it cannot take the cut.
 */
export const pairedQuery = (
  catalog: ReportCatalog,
  query: ReportQuery,
  withKey: string | undefined
): ReportQuery | null => {
  if (!withKey) {
    return null;
  }
  const own = catalog.measures.find((m) => m.key === query.measure);
  const other = catalog.measures.find((m) => m.key === withKey);
  if (!own || !other || !canPair(own, other, query)) {
    return null;
  }
  const { compare: _compare, ...rest } = query;
  return { ...rest, measure: withKey };
};

const dimensionKeys = (result: ReportQueryResult): string =>
  result.columns
    .filter((c) => c.type === "dimension")
    .map((c) => c.key)
    .join("|");

/**
 * Whether a second answer lines up with the first: the same window, grain
 * and dimensions. While one of them is still the previous question's, it
 * must not be drawn against the other.
 */
export const pairFits = (
  primary: ReportQueryResult,
  paired: ReportQueryResult
): boolean =>
  primary.meta.from === paired.meta.from &&
  primary.meta.to === paired.meta.to &&
  primary.meta.grain === paired.meta.grain &&
  dimensionKeys(primary) === dimensionKeys(paired);

/** Points by bucket, to read the second measure beside the first's. */
export const seriesByBucket = (
  series: ReportPoint[]
): Map<string, ReportPoint> => new Map(series.map((p) => [p.bucket, p]));

/** Rows by their joined keys, to read the second measure beside the first's. */
export const rowsByKey = (rows: ReportRow[]): Map<string, ReportRow> =>
  new Map(rows.map((r) => [r.keys.join("|"), r]));

/** The keys of a pair drawn as two slices of a pie. */
export const OWN_KEY = "own";
export const PAIRED_KEY = "paired";

/** A pair's totals as two rows, for a pie of two slices. */
export const pairSlices = (
  measure: ReportMeasure,
  ownTotal: number,
  paired: PairedResult
): { key: string; label: string; value: number }[] => [
  { key: OWN_KEY, label: measure.label, value: ownTotal },
  {
    key: PAIRED_KEY,
    label: paired.measure.label,
    value: paired.result.totals.value
  }
];

/**
 * The second measure as a share of the first ("612 resolved of 686 filed" is
 * 0.89), for a gauge. Null when there is nothing to compare against.
 */
export const pairRatio = (
  ownTotal: number,
  pairedTotal: number
): number | null => (ownTotal > 0 ? pairedTotal / ownTotal : null);

/**
 * The second measure's value for a row: the matching row's, zero for a
 * count with no such row, and for the row that folds the rest, what the
 * kept rows leave of the second measure's rows (only a count adds up).
 */
export const pairedRowValue = (
  paired: PairedResult,
  byKey: Map<string, ReportRow>,
  row: { key: string; isOther: boolean },
  keptKeys: Set<string>
): number | null => {
  if (!row.isOther) {
    const match = byKey.get(row.key);
    return match ? match.value : paired.measure.kind === "count" ? 0 : null;
  }
  if (paired.measure.kind !== "count") {
    return null;
  }
  return paired.result.rows
    .filter((r) => !keptKeys.has(r.keys.join("|")))
    .reduce((sum, r) => sum + r.value, 0);
};
